import { useEffect, useState } from 'react';
import { Star, Loader2, MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Reveal } from '@/components/ui/Reveal';
import { StarRating } from '@/components/ui/StarRating';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Avis() {
  useSEO({
    title: "Avis clients | Groupe Ambulances Beaulon",
    description: "Découvrez les avis de nos clients sur le Groupe Ambulances Beaulon. Transport sanitaire de qualité dans l'Allier.",
    keywords: "avis ambulance allier, témoignages ambulance moulins, satisfaction client transport sanitaire"
  });

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ author: '', rating: 5, comment: '', context: '' });

  useEffect(() => {
    api.get('/avis').then(r => setReviews(r.data)).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/avis', form);
      toast.success('Merci ! Votre avis sera publié après modération.');
      setDialogOpen(false);
      setForm({ author: '', rating: 5, comment: '', context: '' });
    } catch (err) {
      toast.error("Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : '5.0';

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10">
          <Reveal>
            <p className="badge-soft mb-3"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Avis clients</p>
            <h1 className="h1 text-anthracite-950">Ce que disent nos clients</h1>
            <p className="lead mt-4 max-w-3xl">La satisfaction de nos patients est notre meilleure récompense. Découvrez les témoignages de celles et ceux que nous accompagnons.</p>
            <div className="mt-6 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="text-5xl font-heading font-semibold text-anthracite-950">{avg}</div>
                <div>
                  <StarRating rating={Math.round(Number(avg))} size="md" />
                  <p className="text-xs text-muted-foreground mt-1">Basé sur {reviews.length} avis publiés</p>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="avis-add-button"><MessageSquarePlus className="h-4 w-4" /> Laisser un avis</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Partagez votre expérience</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submit} className="space-y-4">
                    <div>
                      <Label>Votre nom ou prénom *</Label>
                      <Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} required data-testid="avis-author-input" />
                    </div>
                    <div>
                      <Label>Note *</Label>
                      <div className="mt-1 flex gap-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))} className="p-1" aria-label={`${n} étoile${n > 1 ? 's' : ''}`} data-testid={`avis-star-${n}`}>
                            <Star className={`h-7 w-7 ${n <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-anthracite-200'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Contexte (ex: dialyse - Moulins)</Label>
                      <Input value={form.context} onChange={e => setForm(f => ({ ...f, context: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Votre avis *</Label>
                      <Textarea value={form.comment} onChange={e => setForm(f => ({ ...f, comment: e.target.value }))} required className="min-h-[120px]" data-testid="avis-comment-input" />
                    </div>
                    <p className="text-xs text-muted-foreground">Votre avis sera publié après modération.</p>
                    <Button type="submit" disabled={submitting} className="w-full" data-testid="avis-submit-button">
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : 'Envoyer mon avis'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-700" /></div>
          ) : reviews.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Aucun avis pour le moment.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={i * 40}>
                  <article className="card-base p-5 h-full flex flex-col" data-testid="review-card">
                    <div className="flex items-center justify-between">
                      <StarRating rating={r.rating} />
                      {r.is_google && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-anthracite-100 text-anthracite-700">Google</span>}
                    </div>
                    <p className="text-sm text-anthracite-900 leading-relaxed mt-3 flex-1">« {r.comment} »</p>
                    <div className="mt-4 pt-3 border-t flex items-center justify-between">
                      <p className="text-sm font-medium text-anthracite-900">{r.author}</p>
                      {r.context && <p className="text-xs text-muted-foreground">{r.context}</p>}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
